import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, Clock } from "lucide-react";
import { paymentStatus } from "@/api";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ loading: true, paid: false, reference: "", kind: "rental_deposit" });

  useEffect(() => {
    if (!sessionId) {
      setState({ loading: false, paid: false, reference: "", kind: "rental_deposit" });
      return;
    }
    let attempts = 0;
    const poll = async () => {
      try {
        const res = await paymentStatus(sessionId);
        if (res.payment_status === "paid") {
          setState({ loading: false, paid: true, reference: res.reference, kind: res.kind || "rental_deposit" });
          return;
        }
      } catch { /* keep polling */ }
      attempts += 1;
      if (attempts < 15) setTimeout(poll, 2000);
      else setState({ loading: false, paid: false, reference: "", kind: "rental_deposit" });
    };
    poll();
  }, [sessionId]);

  const isInstalment = state.kind === "rto_instalment";
  const isSettlement = state.kind === "settlement";
  const title = isSettlement ? "It's yours." : isInstalment ? "Instalment paid." : "Deposit paid.";
  const subline = isSettlement
    ? "Your early settlement is confirmed — the ownership balance is zero and the vehicle will be registered in your name."
    : isInstalment
      ? "Your monthly instalment is confirmed and logged to your ownership progress."
      : "Your security deposit is confirmed";
  const footnote = isSettlement
    ? "CompCar Motors will be in touch to complete the registration transfer. Congratulations!"
    : isInstalment
      ? "Sign back into the client portal anytime to see your updated balance and statement."
      : "CompCar Motors will confirm your collection time shortly. The deposit is refundable per your rental terms.";

  return (
    <div data-testid="payment-success-page" className="min-h-screen flex items-center justify-center px-4 pt-20">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} className="text-center max-w-md">
        {state.loading ? (
          <>
            <Loader2 size={56} className="text-sky-300 mx-auto mb-6 animate-spin" />
            <h1 className="font-display text-3xl font-black text-white mb-3">Confirming your payment…</h1>
            <p className="text-slate-400 text-sm">This takes a few seconds. Please don't close this page.</p>
          </>
        ) : state.paid ? (
          <>
            <CheckCircle2 size={64} className="text-emerald-400 mx-auto mb-8" />
            <h1 className="font-display text-4xl font-black text-white mb-4">{title}</h1>
            <p className="text-slate-400 text-sm leading-relaxed mb-2">
              {subline}
              {state.reference && <> Reference <strong className="text-[#F57C00]">{state.reference}</strong></>}.
            </p>
            <p className="text-slate-500 text-xs mb-8">{footnote}</p>
            <Link to={isInstalment || isSettlement ? "/portal" : "/"} data-testid="payment-success-home" className="inline-block bg-[#F57C00] hover:bg-[#E67300] text-white font-bold px-8 py-4 rounded-md transition-colors">
              {isInstalment || isSettlement ? "Back to your portal" : "Back to CompCar Motors"}
            </Link>
          </>
        ) : (
          <>
            <Clock size={56} className="text-amber-400 mx-auto mb-6" />
            <h1 className="font-display text-3xl font-black text-white mb-3">Payment still processing</h1>
            <p className="text-slate-400 text-sm mb-8">If you completed payment, it may take a moment to reflect. Call 010 534 9926 if you're unsure.</p>
            <Link to="/" className="text-sky-300 hover:text-white font-bold text-sm">Back to homepage</Link>
          </>
        )}
      </motion.div>
    </div>
  );
}
